import {Annotation, AnnotationsPlugin, GroupedBarsPlugin, GroupedBarsPluginOptions} from "uplot-vplugins";
import {GetGroupedBarsPluginConfig, GroupedBarDisplayCalc} from "./GroupedBarDisplayCalc.js";
import {AggregationData, GetValMultiplierForGroupMetricNormalization} from "./AggregationData.js";
import {ALL_GROUP_NAME} from "./MetricCollector.js";
import {ShowBarText} from "./StatsChartOptions.js";
import {GetAnchorStatDayOffset, GetStatNightContainingX} from "./Helpers.js";
import {StatsXType, StatsYType} from "../../Server/DB/statViews/@StatTypes.js";
import {store} from "../../Store/index.js";
import {MarkerFromMarkerOrDate, ShowTechExecution} from "../../Server/DB/statViews/@StatView.js";

export function PopulateAnnotationsForChart(args: {
	width: number, height: number, chartOptions: uPlot.Options, annotations: Annotation[],
	xValues: number[], groups: string[], yValuesPerGroup: Map<string, number[]>, aggregationData: AggregationData
}) {
	const {width, height, chartOptions, annotations, xValues, groups, yValuesPerGroup, aggregationData} = args;
	const {view} = store.main.stats;

	// rect calculators (for bars only atm); synchronous calc'ing is nicer, plus had issues with uplot loopback
	//const plotArea = this.chart.current.bbox;
	//const plotArea_width = chartBodyRect.width;
	const chartLeftPadding = chartOptions.axes!.filter(a=>a.scale != "x" && a.side != 1)
		.map(a=>(a.labelSize as number) + (a.size as number)).Sum();
	const chartRightPadding = chartOptions.axes!.filter(a=>a.scale != "x" && a.side == 1)
		.map(a=>(a.labelSize as number) + (a.size as number)).Sum().KeepAtLeast(25);
	const plotAreaWidth_fromWidthAndPaddings = width - (chartLeftPadding + chartRightPadding);
	const barRectCalculator = new GroupedBarDisplayCalc({xValueCount: xValues.length, dataGroupCount: groups.length, config: GetGroupedBarsPluginConfig(), plotAreaWidth: plotAreaWidth_fromWidthAndPaddings});

	if (view.renderType == "bars") {
		const xValueLucidSumSamples = xValues.map(xValue=>aggregationData.segmentIsLucidSamples.samplesForEachXValue.get(xValue) ?? []);
		const xValueTechExecutionSamples = xValues.map(xValue=>aggregationData.segmentTechExecutionSamples.samplesForEachXValue.get(xValue) ?? []);
		for (const [index, xValue] of xValues.entries()) {
			const lucidSumSamples = xValueLucidSumSamples[index];
			for (const [i, group] of groups.entries()) {
				const barCenterX = barRectCalculator.GetBarRect(index, i).center;

				const lucidSumSamplesForGroup = lucidSumSamples.filter(a=>group == ALL_GROUP_NAME ? true : a.group == group);
				const lucidSum = lucidSumSamplesForGroup.map(a=>a.value).Sum();

				const techExecSamplesForGroup = xValueTechExecutionSamples[index].filter(a=>group == ALL_GROUP_NAME ? true : a.group == group);

				const segmentPercentThatIsLucid_raw = lucidSum / lucidSumSamplesForGroup.length;
				const segmentPercentThatIsLucid_final = segmentPercentThatIsLucid_raw * GetValMultiplierForGroupMetricNormalization(view, group, "lucidityRate");

				const nightSessionsForXValue = aggregationData.sessionsForEachXValue.get(xValue)?.filter(a=>a.launchType == "night") ?? [];
				const nightCount = nightSessionsForXValue.map(a=>GetStatNightContainingX(a.startTime)).Distinct().length;

				const baseLines = ShowBarText() ? 1 : 0;
				const getOffsetForLabelShiftedXUp = (linesShiftedUp: number)=>(-5 - (10 * linesShiftedUp)) * window.devicePixelRatio;
				const useLongLabels = view.showNightCount; // if night-count shown (which is always long), infer that user wants long labels for all

				if (view.showTechExecution && view.showTechExecution != ShowTechExecution.hidden) {
					const techExecValues = techExecSamplesForGroup.map(a=>a.value).filter(a=>a != null) as number[];
					const maxTechExec = techExecValues.length > 0 ? Math.max(...techExecValues) : null;
					const minForPass = (view.techExecution_minForPass != null && view.techExecution_minForPass != 0) ? view.techExecution_minForPass : 1;
					const techExecText = view.showTechExecution == ShowTechExecution.percent
						? (maxTechExec != null ? `T:${(maxTechExec * 100).RoundTo(1)}%` : null)
						: (techExecValues.some(a=>a >= minForPass) ? "T✔" : null);
					if (techExecText != null) {
						annotations.push({
							type: "text",
							x: {pixel: barCenterX * window.devicePixelRatio},
							y: {
								value: yValuesPerGroup.get(group)![index],
								finalize: y=>y + getOffsetForLabelShiftedXUp(baseLines + (view.showLucidCount ? 1 : 0) + (view.showLucidRate ? 1 : 0) + (view.showNightCount ? 1 : 0)),
							},
							text: techExecText,
							fillStyle: "rgba(200,100,255,1)",
							font: `${10 * window.devicePixelRatio}px Arial`,
						});
					}
				}
				if (view.showLucidCount && lucidSum > 0) {
					annotations.push({
						type: "text",
						x: {pixel: barCenterX * window.devicePixelRatio},
						y: {
							value: yValuesPerGroup.get(group)![index],
							finalize: y=>y + getOffsetForLabelShiftedXUp(baseLines + (view.showLucidRate ? 1 : 0) + (view.showNightCount ? 1 : 0)),
						},
						text: `${lucidSum}` + (useLongLabels ? ` lucid${lucidSum == 1 ? "" : "s"}` : ""),
						fillStyle: "rgba(255,255,0,1)",
						font: `${10 * window.devicePixelRatio}px Arial`,
					});
				}
				if (view.showLucidRate && lucidSum > 0) {
					annotations.push({
						type: "text",
						x: {pixel: barCenterX * window.devicePixelRatio},
						y: {
							value: yValuesPerGroup.get(group)![index],
							finalize: y=>y + getOffsetForLabelShiftedXUp(baseLines + (view.showNightCount ? 1 : 0)),
						},
						text: `${segmentPercentThatIsLucid_final.ToPercentStr(0)}` + (useLongLabels ? ` of cycles lucid` : ""),
						fillStyle: "rgba(255,255,0,1)",
						font: `${10 * window.devicePixelRatio}px Arial`,
					});
				}
				if (view.showNightCount) {
					annotations.push({
						type: "text",
						x: {pixel: barCenterX * window.devicePixelRatio},
						y: {
							value: yValuesPerGroup.get(group)![index],
							finalize: y=>y + getOffsetForLabelShiftedXUp(baseLines),
						},
						text: `(${nightCount} night${nightCount == 1 ? "" : "s"})`,
						//fillStyle: "rgba(255,255,0,1)",
						fillStyle: "rgba(255,255,255,1)",
						font: `${10 * window.devicePixelRatio}px Arial`,
					});
				}
			}
		}
	}

	if (view.showDateMarkers && view.xType == "dayOffset") {
		for (const markerOrDate of (view.dateMarkers ?? [])) {
			const marker = MarkerFromMarkerOrDate(markerOrDate);
			if (marker?.date == null) continue;
			const xValue = GetAnchorStatDayOffset(marker.date, "day");
			const xValueIndex = xValues.indexOf(xValue);
			const prevBarRight = xValueIndex > 0 ? barRectCalculator.GetBarRect(xValueIndex - 1, groups.length - 1).right : null;
			const barLeft = barRectCalculator.GetBarRect(xValueIndex, 0).left;
			const centerOfGap = prevBarRight != null ? (prevBarRight + barLeft) / 2 : barLeft;
			//console.log("XVal:", xValue, "Date:", new Date(date), "CenterOfGap:", centerOfGap, prevBarRight, barLeft);

			/*annotations.push({
				type: "line",
				x: {value: xValue},
				color: "rgba(255,255,0,.7)",
				lineWidth: 1,
			});*/
			annotations.push({
				type: "box",
				//xMin: {value: xValue, value_toCanvasPixels: true},
				//xMax: {value: xValue + 1},
				xMin: {
					//pixel: GetPercentFromXToY(xValues[0], xValues.Last(), xValue).ToPercentStr(),
					pixel: centerOfGap * window.devicePixelRatio,
					//finalize: a=>a + 5, // put closer to middle of gap (for typical day-count)
				},
				xSize: {
					pixel: 1 * window.devicePixelRatio,
					finalize: ()=>1 * window.devicePixelRatio, // fsr this is needed
				},
				yMin: {pixel: "0%"},
				yMax: {pixel: "100%"},
				fillStyle: marker.color ?? "rgba(255,255,0,.7)",
				drawType: "destination-over",
			});
			//console.log("XValToPos:", this.chart.current?.valToPos(xValue, "x", false), this.chart.current?.valToPos(xValue, "x", true));
		}
	}
}